import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
// import { Helmet } from "react-helmet"; // Will add when package is installed
import ScrollAnimation from "react-animate-on-scroll";
import DynamicBlogLoader from "../utils/dynamicBlogLoader";
import ImageHelper from "../utils/imageHelper";
import HeaderStatic from "../components/layouts/HeaderStatic";
import Footer from "../components/layouts/Footer";
import Pagetitle from "../components/elements/Pagetitle";

function BlogListPage() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Navigation state
  const [toggleMenu, setToggleMenu] = useState(false);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  useEffect(() => {
    loadBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, selectedCategory, selectedTag, searchQuery, sortBy]);

  const headerToggler = (e) => {
    e.preventDefault();
    setToggleMenu(!toggleMenu);
  };

  // Close menu when clicking on content
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest(".content")) {
        setToggleMenu(false);
      }
    };
    
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const allBlogs = await DynamicBlogLoader.loadAllBlogs();
      setBlogs(allBlogs);
      
      const allCategories = await DynamicBlogLoader.getAllCategories();
      const allTags = await DynamicBlogLoader.getAllTags();
      
      setCategories(allCategories);
      setTags(allTags);
      setError(null);
    } catch (err) {
      setError('Không thể tải danh sách bài viết');
      console.error('Error loading blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = () => {
    let filtered = [...blogs];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(blog => 
        blog.metadata.category === selectedCategory
      );
    }
    
    // Filter by tag
    if (selectedTag !== 'all') {
      filtered = filtered.filter(blog => 
        blog.metadata.tags.includes(selectedTag)
      );
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(blog =>
        blog.metadata.title.toLowerCase().includes(query) ||
        blog.metadata.description.toLowerCase().includes(query) ||
        blog.content.toLowerCase().includes(query) ||
        blog.metadata.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Sort blogs
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.metadata.title.localeCompare(b.metadata.title);
        case 'category':
          return a.metadata.category.localeCompare(b.metadata.category);
        case 'reading-time':
          return a.readingTime - b.readingTime;
        case 'date':
        default:
          return new Date(b.metadata.date) - new Date(a.metadata.date);
      }
    });
    
    setFilteredBlogs(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const formatDate = (dateString) => {
    return DynamicBlogLoader.formatDate(dateString);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Technology': '#667eea',
      'Business': '#f093fb',
      'Crypto': '#4ecdc4',
      'Startup': '#ff6b6b',
      'Tutorial': '#45b7d1',
      'Reviews': '#96ceb4'
    };
    return colors[category] || '#6c757d';
  };

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedTag('all');
    setSearchQuery('');
    setSortBy('date');
  };

  if (loading) {
    return (
      <>
        <HeaderStatic
          logoSource="/images/logo.svg"
          toggleMenu={toggleMenu}
          headerToggler={headerToggler}
        />
        <main className={toggleMenu ? "content open" : "content"}>
          <div className="blog-list-page">
            <div className="container">
              <div className="loading-container">
                <div className="loading-spinner">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Đang tải...</span>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <HeaderStatic
        logoSource="/images/logo.svg"
        toggleMenu={toggleMenu}
        headerToggler={headerToggler}
      />
      <main className={toggleMenu ? "content open" : "content"}>
        <div className="blog-list-page">
          {/* SEO Meta tags - will be added when react-helmet is installed */}

          <section className="blog-list-main">
        <div className="container">
          <ScrollAnimation
            animateIn="fadeInUp"
            animateOut="fadeInOut"
            animateOnce={true}
          >
            <Pagetitle title="Bài Viết" />
          </ScrollAnimation>

          <ScrollAnimation
            animateIn="fadeInUp"
            animateOut="fadeInOut"
            animateOnce={true}
            delay={200}
          >
            <div className="blog-filters">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <div className="filter-controls">
                    <div className="filter-group">
                      <label>Danh mục:</label>
                      <select 
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="form-control"
                      >
                        <option value="all">Tất cả</option>
                        {categories.map(category => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label>Tag:</label>
                      <select 
                        value={selectedTag}
                        onChange={(e) => setSelectedTag(e.target.value)}
                        className="form-control"
                      >
                        <option value="all">Tất cả</option>
                        {tags.map(tag => (
                          <option key={tag} value={tag}>
                            #{tag}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label>Sắp xếp:</label>
                      <select 
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="form-control"
                      >
                        <option value="date">Ngày mới nhất</option>
                        <option value="title">Tiêu đề A-Z</option>
                        <option value="category">Danh mục</option>
                        <option value="reading-time">Thời gian đọc</option>
                      </select>
                    </div>
                    
                    <button 
                      onClick={clearFilters}
                      className="btn btn-outline-secondary btn-sm"
                    >
                      <i className="fas fa-times"></i>
                      Xóa bộ lọc
                    </button>
                  </div>
                </div>
                
                <div className="col-lg-4">
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Tìm kiếm bài viết..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="form-control"
                    />
                    <i className="fas fa-search search-icon"></i>
                  </div>
                </div>
              </div>
              
              <div className="filter-stats">
                <p>
                  Hiển thị {currentBlogs.length} trong tổng số {filteredBlogs.length} bài viết
                  {(selectedCategory !== 'all' || selectedTag !== 'all' || searchQuery) && (
                    <span className="filter-indicator"> (đã lọc)</span>
                  )}
                </p>
              </div>
            </div>
          </ScrollAnimation>

          {error ? (
            <div className="text-center">
              <div className="alert alert-warning">
                <p>{error}</p>
                <button onClick={loadBlogs} className="btn btn-primary btn-sm">
                  Thử lại
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="blog-grid">
                <div className="row">
                  {currentBlogs.length > 0 ? (
                    currentBlogs.map((blog, index) => (
                      <div key={blog.id} className="col-lg-4 col-md-6">
                        <ScrollAnimation
                          animateIn="fadeInUp"
                          animateOut="fadeInOut"
                          animateOnce={true}
                          delay={index * 100}
                        >
                          <div className="blog-card">
                            <div className="blog-image-wrapper">
                              <Link to={`/bai-viet/${blog.slug}`}>
                                <img 
                                  src={blog.metadata.image} 
                                  alt={blog.metadata.title}
                                  className="blog-image"
                                  onError={ImageHelper.createImageErrorHandler({
                                    width: 400,
                                    height: 200,
                                    text: blog.metadata.category,
                                    color: getCategoryColor(blog.metadata.category)
                                  })}
                                />
                              </Link>
                              <div className="blog-overlay">
                                <div className="blog-category">
                                  <span 
                                    className="category-badge"
                                    style={{ backgroundColor: getCategoryColor(blog.metadata.category) }}
                                  >
                                    {blog.metadata.category}
                                  </span>
                                </div>
                                {blog.metadata.featured && (
                                  <div className="featured-badge">
                                    <i className="fas fa-star"></i>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="blog-content">
                              <div className="blog-meta">
                                <span className="blog-date">
                                  <i className="far fa-calendar-alt"></i>
                                  {formatDate(blog.metadata.date)}
                                </span>
                                <span className="reading-time">
                                  <i className="far fa-clock"></i>
                                  {blog.readingTime} phút đọc
                                </span>
                              </div>
                              
                              <h3 className="blog-title">
                                <Link to={`/bai-viet/${blog.slug}`}>
                                  {blog.metadata.title}
                                </Link>
                              </h3>
                              
                              <p className="blog-excerpt">
                                {blog.metadata.description || blog.excerpt}
                              </p>
                              
                              <div className="blog-tags">
                                {blog.metadata.tags.slice(0, 3).map(tag => (
                                  <span 
                                    key={tag} 
                                    className="blog-tag"
                                    onClick={() => setSelectedTag(tag)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                              
                              <div className="blog-footer">
                                <div className="blog-author">
                                  <span>
                                    <i className="far fa-user"></i>
                                    {blog.metadata.author}
                                  </span>
                                </div>
                                <Link 
                                  to={`/bai-viet/${blog.slug}`}
                                  className="read-more-btn"
                                >
                                  Đọc tiếp
                                  <i className="fas fa-arrow-right"></i>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </ScrollAnimation>
                      </div>
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="no-blogs-found text-center">
                        <i className="fas fa-search fa-3x text-muted"></i>
                        <h3>Không tìm thấy bài viết</h3>
                        <p>Hãy thử thay đổi bộ lọc hoặc từ khóa tìm kiếm.</p>
                        <button 
                          onClick={clearFilters}
                          className="btn btn-primary"
                        >
                          Xóa bộ lọc
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {totalPages > 1 && (
                <ScrollAnimation
                  animateIn="fadeInUp"
                  animateOut="fadeInOut"
                  animateOnce={true}
                >
                  <div className="pagination-wrapper">
                    <nav aria-label="Blog pagination">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>
                        
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          return (
                            <li 
                              key={pageNumber} 
                              className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(pageNumber)}
                              >
                                {pageNumber}
                              </button>
                            </li>
                          );
                        })}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </ScrollAnimation>
              )}
            </>
          )}
            </div>
          </section>
          
          <Footer />
        </div>
      </main>
    </>
  );
}

export default BlogListPage;